import React, { useCallback, useEffect, useRef, useState } from 'react';
import type {
  VoiceAgentOption,
  VoiceConversationCostRollup,
} from '../../client';
import { useVoiceChannel } from '../../voice/useVoiceChannel';
import type { VoiceModelState } from '../../voice/voice-types';
import VoiceAgentPickerModal from './VoiceAgentPickerModal';
import VoiceWaveform from './VoiceWaveform';
import './voice-deck.css';
import { formatVoiceSessionCost, type VoiceCostState } from './voiceCost';

interface VoiceDeckProps {
  teamId: number;
  conversationId: number;
  agents: VoiceAgentOption[];
  isProcessing: boolean;
  approvalPending: boolean;
  onEnd: () => void;
  voiceCost?: VoiceConversationCostRollup | null;
  voiceCostState?: VoiceCostState;
}

const MODEL_STATE_LABELS: Record<VoiceModelState, string> = {
  listening: 'Listening',
  recording: 'Recording',
  transcribing: 'Transcribing',
  thinking: 'Thinking',
  awaiting_tool_result: 'Working',
  awaiting_approval: 'Awaiting approval',
  synthesizing: 'Synthesizing',
  speaking: 'Speaking',
  paused: 'Paused',
};

const VoiceDeck: React.FC<VoiceDeckProps> = ({
  teamId,
  conversationId,
  agents,
  isProcessing,
  approvalPending,
  onEnd,
  voiceCost = null,
  voiceCostState = 'loading',
}) => {
  const voice = useVoiceChannel(conversationId);
  const currentEligibleAgent = voice.isCurrentConversation
    ? agents.find((agent) => agent.agentId === voice.session?.currentAgentId)
    : undefined;
  const [selectedAgentId, setSelectedAgentId] = useState(
    currentEligibleAgent?.agentId ??
      (agents.length === 1 ? agents[0].agentId : 0),
  );
  const [switching, setSwitching] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pttActive, setPttActive] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [bargeInVisible, setBargeInVisible] = useState(false);

  useEffect(() => {
    if (!voice.bargeInAt) {
      setBargeInVisible(false);
      return;
    }
    setBargeInVisible(true);
    const timer = window.setTimeout(() => setBargeInVisible(false), 5_000);
    return () => window.clearTimeout(timer);
  }, [voice.bargeInAt]);
  const pttActiveRef = useRef(false);
  const pttAuthorityRef = useRef('');
  const eligibilityGenerationRef = useRef(0);
  const endVoiceRef = useRef(voice.end);
  const openVoiceRef = useRef(voice.open);
  const terminalCloseRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const current = agents.find(
      (agent) => agent.agentId === voice.session?.currentAgentId,
    );
    if (current) setSelectedAgentId(current.agentId);
    else if (voice.isCurrentConversation && voice.session) {
      setSelectedAgentId(0);
    } else if (
      agents.length > 0 &&
      !agents.some((agent) => agent.agentId === selectedAgentId)
    ) {
      setSelectedAgentId(agents.length === 1 ? agents[0].agentId : 0);
    }
  }, [agents, selectedAgentId, voice.session?.currentAgentId]);

  const selectedAgent = agents.find(
    (agent) => agent.agentId === selectedAgentId,
  );
  const needsAgentSelection = !selectedAgent;
  const mode =
    voice.session?.profileSnapshot.profile.mode ??
    selectedAgent?.mode ??
    'realtime';
  const pttAuthority = voice.isCurrentConversation
    ? `${voice.session?.sessionId ?? ''}:${voice.session?.currentAgentId ?? ''}`
    : '';

  const reportActionError = useCallback((error: unknown): void => {
    setActionError(
      error instanceof Error ? error.message : 'Voice action failed',
    );
  }, []);

  const cancelPtt = useCallback((): void => {
    const wasActive = pttActiveRef.current;
    pttActiveRef.current = false;
    pttAuthorityRef.current = '';
    setPttActive(false);
    if (wasActive) void voice.cancelPtt().catch(reportActionError);
  }, [reportActionError, voice.cancelPtt]);

  useEffect(() => {
    endVoiceRef.current = voice.end;
    openVoiceRef.current = voice.open;
  }, [voice.end, voice.open]);

  const activeAgentIsIneligible = Boolean(
    agents.length > 0 &&
    voice.isCurrentConversation &&
    voice.session &&
    !agents.some((agent) => agent.agentId === voice.session?.currentAgentId),
  );
  const eligibleAgentIds = agents.map((agent) => agent.agentId).join(',');

  useEffect(() => {
    if (!activeAgentIsIneligible) return;
    const generation = ++eligibilityGenerationRef.current;
    cancelPtt();
    setSelectedAgentId(0);
    setSwitching(true);
    setActionError(null);
    void (async () => {
      try {
        await endVoiceRef.current();
        if (eligibilityGenerationRef.current !== generation) return;
        const replacement = agents.length === 1 ? agents[0] : undefined;
        setSelectedAgentId(replacement?.agentId ?? 0);
        if (replacement) {
          await openVoiceRef.current({
            teamId,
            agentId: replacement.agentId,
          });
        } else {
          setPickerOpen(true);
        }
      } catch (error: unknown) {
        if (eligibilityGenerationRef.current === generation) {
          reportActionError(error);
        }
      } finally {
        if (eligibilityGenerationRef.current === generation) {
          setSwitching(false);
        }
      }
    })();
    return () => {
      if (eligibilityGenerationRef.current === generation) {
        eligibilityGenerationRef.current += 1;
      }
    };
  }, [
    activeAgentIsIneligible,
    cancelPtt,
    eligibleAgentIds,
    agents,
    reportActionError,
    teamId,
  ]);

  const beginPtt = useCallback((): void => {
    if (pttActiveRef.current) return;
    pttActiveRef.current = true;
    pttAuthorityRef.current = pttAuthority;
    setPttActive(true);
    setActionError(null);
    void voice.beginPtt().catch((error: unknown) => {
      pttActiveRef.current = false;
      setPttActive(false);
      reportActionError(error);
    });
  }, [pttAuthority, reportActionError, voice.beginPtt]);

  const finishPtt = useCallback((): void => {
    if (!pttActiveRef.current) return;
    pttActiveRef.current = false;
    pttAuthorityRef.current = '';
    setPttActive(false);
    void voice.commitPtt().catch(reportActionError);
  }, [reportActionError, voice.commitPtt]);

  useEffect(() => {
    if (
      pttActiveRef.current &&
      (voice.status !== 'active' || pttAuthorityRef.current !== pttAuthority)
    ) {
      cancelPtt();
    }
  }, [cancelPtt, pttAuthority, voice.status]);

  const completeTerminalClose = useCallback((): void => {
    const closeDeck = terminalCloseRef.current;
    terminalCloseRef.current = null;
    closeDeck?.();
  }, []);

  useEffect(() => {
    if (voice.status === 'ended') completeTerminalClose();
  }, [completeTerminalClose, voice.status]);

  useEffect(() => {
    if (mode !== 'push_to_talk') return;
    const isInteractiveTarget = (target: EventTarget | null): boolean => {
      const element = target instanceof HTMLElement ? target : null;
      return Boolean(
        element?.closest(
          'button, a, input, textarea, select, [contenteditable="true"], [role="button"]',
        ),
      );
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (
        (event.code !== 'Space' && event.key !== ' ') ||
        event.repeat ||
        isInteractiveTarget(event.target)
      )
        return;
      event.preventDefault();
      beginPtt();
    };
    const onKeyUp = (event: KeyboardEvent): void => {
      if (event.code !== 'Space' && event.key !== ' ') return;
      if (!pttActiveRef.current) return;
      event.preventDefault();
      finishPtt();
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', cancelPtt);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', cancelPtt);
      cancelPtt();
    };
  }, [beginPtt, cancelPtt, finishPtt, mode]);

  const changeAgent = async (agentId: number): Promise<void> => {
    if (switching) return;
    if (agentId === selectedAgentId && voice.session) {
      setPickerOpen(false);
      return;
    }
    cancelPtt();
    setSwitching(true);
    setActionError(null);
    try {
      if (voice.isCurrentConversation && voice.session) {
        await voice.switchAgent(agentId);
        setSelectedAgentId(agentId);
      } else {
        setSelectedAgentId(agentId);
        await voice.open({ teamId, agentId });
      }
      setPickerOpen(false);
    } catch (error: unknown) {
      reportActionError(error);
    } finally {
      setSwitching(false);
    }
  };

  const statusLabel = (() => {
    if (needsAgentSelection) return 'Choose an agent';
    if (voice.status === 'opening') return 'Connecting';
    if (voice.status === 'reconnecting') return 'Reconnecting';
    if (voice.status === 'error') return 'Voice chat failed';
    if (voice.status === 'paused') return 'Paused';
    if (voice.status === 'ended') return 'Voice chat ended';
    if (approvalPending) return 'Awaiting approval';
    if (voice.muted) return 'Muted';
    if (pttActive) return 'Recording';
    if (mode === 'push_to_talk' && voice.modelState === 'speaking') {
      return 'Playing';
    }
    if (voice.modelState) return MODEL_STATE_LABELS[voice.modelState];
    if (voice.outputLevel > 0) {
      return mode === 'realtime' ? 'Speaking' : 'Playing';
    }
    if (isProcessing) return mode === 'realtime' ? 'Thinking' : 'Working';
    return mode === 'realtime' ? 'Listening' : 'Ready to record';
  })();

  const run = (action: () => Promise<void>): void => {
    setActionError(null);
    void action().catch(reportActionError);
  };

  const runTerminalAction = (
    action: () => Promise<void>,
    closeDeck: () => void,
  ): void => {
    setActionError(null);
    terminalCloseRef.current = closeDeck;
    void action().catch(reportActionError).finally(completeTerminalClose);
  };

  const channelEnded = voice.status === 'ended';
  const mediaReady = voice.status === 'active';
  const currentSessionCost = voice.session
    ? voiceCost?.sessions.find(
        (session) => session.sessionId === voice.session?.sessionId,
      )
    : undefined;
  const formattedCost = formatVoiceSessionCost(
    currentSessionCost,
    voiceCostState,
    !channelEnded && voice.status !== 'error',
  );

  return (
    <section className="voice-deck" aria-label="Voice chat controls">
      <div className="voice-deck-operations">
        <div className="voice-agent-selector">
          {agents.length > 1 ? (
            <button
              type="button"
              className="voice-agent-selector-button"
              disabled={switching}
              onClick={() => setPickerOpen(true)}>
              {selectedAgent?.agentName ?? 'Choose an agent'}
            </button>
          ) : (
            <strong>{selectedAgent?.agentName ?? 'Choose an agent'}</strong>
          )}
          <div
            className="voice-deck-cost"
            role="group"
            aria-label="Voice chat cost"
            aria-live="polite">
            <span>{formattedCost.audio}</span>
          </div>
        </div>
        <div className="voice-deck-status" role="status" aria-live="polite">
          {statusLabel}
        </div>
        {bargeInVisible && (
          <div className="voice-deck-barge-in" role="status" aria-live="polite">
            Interrupted — the agent finishes its reply in chat
          </div>
        )}
      </div>

      <VoiceWaveform
        inputLevel={mediaReady ? voice.inputLevel : 0}
        outputLevel={mediaReady ? voice.outputLevel : 0}
        inputSpectrum={mediaReady ? voice.inputSpectrum : null}
        outputSpectrum={mediaReady ? voice.outputSpectrum : null}
      />

      {approvalPending && (
        <p className="voice-deck-approval">
          Review the approval card to continue.
        </p>
      )}
      {voice.mediaWarning && (
        <p className="voice-deck-warning" role="note">
          {voice.mediaWarning}
        </p>
      )}
      {(actionError || voice.error) && (
        <p className="voice-deck-error" role="alert">
          {actionError || voice.error}
        </p>
      )}

      <div className="voice-deck-controls">
        {!needsAgentSelection &&
          !channelEnded &&
          mode === 'push_to_talk' &&
          mediaReady &&
          voice.status !== 'paused' && (
            <button
              type="button"
              className="voice-deck-ptt"
              aria-label="Hold to talk"
              aria-pressed={pttActive}
              disabled={voice.muted}
              onPointerDown={(event) => {
                event.currentTarget.setPointerCapture?.(event.pointerId);
                beginPtt();
              }}
              onPointerUp={(event) => {
                if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
                  event.currentTarget.releasePointerCapture(event.pointerId);
                }
                finishPtt();
              }}
              onPointerCancel={cancelPtt}
              onTouchCancel={cancelPtt}
              onKeyDown={(event) => {
                if (event.code !== 'Space' && event.key !== ' ') return;
                event.preventDefault();
                if (!event.repeat) beginPtt();
              }}
              onKeyUp={(event) => {
                if (event.code !== 'Space' && event.key !== ' ') return;
                event.preventDefault();
                finishPtt();
              }}>
              {pttActive ? 'Release to send' : 'Hold to talk'}
              <span>Space</span>
            </button>
          )}
        {!needsAgentSelection && !channelEnded && voice.status === 'paused' ? (
          <button type="button" onClick={() => run(voice.resume)}>
            Resume
          </button>
        ) : !needsAgentSelection && !channelEnded && voice.muted ? (
          <button
            type="button"
            aria-label="Unmute voice chat"
            onClick={() => voice.setMuted(false)}>
            Unmute
          </button>
        ) : !needsAgentSelection && !channelEnded ? (
          <button
            type="button"
            aria-label="Mute voice chat"
            disabled={!mediaReady}
            onClick={() => {
              cancelPtt();
              voice.setMuted(true);
            }}>
            Mute
          </button>
        ) : null}
        {!needsAgentSelection && !channelEnded && mode === 'realtime' && (
          <button
            type="button"
            aria-label="Stop response"
            title="Stop the agent's current response without ending voice chat"
            disabled={!mediaReady}
            onClick={() => run(voice.interrupt)}>
            Stop response
          </button>
        )}
        {!channelEnded && (
          <button
            type="button"
            className="voice-deck-end"
            onClick={() => {
              cancelPtt();
              runTerminalAction(voice.end, onEnd);
            }}>
            End voice chat
          </button>
        )}
      </div>

      <VoiceAgentPickerModal
        isOpen={pickerOpen}
        agents={agents}
        currentAgentId={selectedAgent?.agentId}
        busy={switching}
        status={actionError}
        onClose={() => {
          if (!switching) setPickerOpen(false);
        }}
        onSelect={(agentId) => void changeAgent(agentId)}
      />
    </section>
  );
};

export default VoiceDeck;
